import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Currency, DEFAULT_CURRENCY, formatCurrency } from '@/lib/currency';
import { useToast } from '@/hooks/use-toast';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => Promise<void>;
  formatAmount: (amount: number | string, options?: {
    showSymbol?: boolean;
    showCode?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }) => string;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  // Load user's preferred currency
  useEffect(() => {
    const loadUserCurrency = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // First try to get from profiles table (for landlords)
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('preferred_currency')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!profileError && profileData?.preferred_currency) {
          setCurrencyState(profileData.preferred_currency as Currency);
          setLoading(false);
          return;
        }

        // Then try tenant_profiles table (for tenants)
        const { data: tenantData, error: tenantError } = await supabase
          .from('tenant_profiles')
          .select('preferred_currency')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!tenantError && tenantData?.preferred_currency) {
          setCurrencyState(tenantData.preferred_currency as Currency);
        }
      } catch (error) {
        console.error('Error loading user currency:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserCurrency();
  }, [user]);

  const setCurrency = async (newCurrency: Currency) => {
    if (!user) {
      setCurrencyState(newCurrency);
      localStorage.setItem('preferred_currency', newCurrency);
      return;
    }

    try {
      setLoading(true);

      // Update profiles table (for landlords)
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ preferred_currency: newCurrency })
        .eq('user_id', user.id);

      // Update tenant_profiles table (for tenants) - this might not exist for all users
      const { error: tenantError } = await supabase
        .from('tenant_profiles')
        .update({ preferred_currency: newCurrency })
        .eq('user_id', user.id);

      // Don't show error if tenant profile doesn't exist
      if (profileError) {
        throw profileError;
      }

      setCurrencyState(newCurrency);
      localStorage.setItem('preferred_currency', newCurrency);
      
      toast({
        title: 'Currency Updated',
        description: `Preferred currency changed to ${newCurrency}`,
      });
    } catch (error: any) {
      console.error('Error updating currency:', error);
      toast({
        title: 'Error',
        description: 'Failed to update currency preference',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (
    amount: number | string,
    options?: {
      showSymbol?: boolean;
      showCode?: boolean;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    }
  ) => {
    return formatCurrency(amount, currency, options);
  };

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        formatAmount,
        loading,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};